import { useCallback, useState } from 'react'
import { SORT_MODES, normalizeSortMode } from '../utils/entitySort'

/**
 * Sort mode remembered per list in localStorage.
 *
 * normalizeSortMode() guards the read: a stale key from an older build, or a
 * hand-edited value, must not leave the list with a mode no comparator knows.
 * Reads are wrapped because localStorage throws in private-mode Safari and
 * when a browser blocks storage entirely — a sort preference is not worth
 * taking the tab down for.
 */
export function usePersistedSortMode(storageKey) {
  const [mode, setMode] = useState(() => {
    try {
      return normalizeSortMode(localStorage.getItem(storageKey))
    } catch {
      return normalizeSortMode(null)
    }
  })

  const update = useCallback(
    (next) => {
      const valid = normalizeSortMode(next)
      setMode(valid)
      try {
        localStorage.setItem(storageKey, valid)
      } catch {
        // Preference just won't survive the reload.
      }
    },
    [storageKey]
  )

  return [mode, update]
}

export default function SortSelect({ id, value, onChange, label = 'Sort' }) {
  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: 'var(--text-muted)',
        fontSize: '0.9rem',
        flexShrink: 0,
      }}
    >
      {label}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          // --bg-secondary, not the --accent-deep the neighbouring filter
          // inputs use: that token is not defined in global.css, so it
          // resolves to nothing and the control inherits whatever is behind it.
          background: 'var(--bg-secondary)',
          border: '1px solid var(--accent-teal)',
          borderRadius: '8px',
          color: 'var(--text-primary)',
          padding: '0.75rem 0.85rem',
          fontSize: '0.95rem',
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {SORT_MODES.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
    </label>
  )
}
